import React from "react";

import {
    Document,
    Page,
    Text,
    View,
    Image,
    Svg,
    Line,
    Polygon,
    Rect,
    Circle,
    Path,
    StyleSheet,
    pdf,
} from "@react-pdf/renderer";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import SCHOOL_LOGO from "./tradeschoollogo.jpg";


const INSTITUTE_NAME =
    "IGNITE S VOCATIONAL SCHOOL";

const INSTITUTE_SUBTITLE =
    "TRADE TRAINING • PRACTICAL SKILLS • PROFESSIONAL EXCELLENCE";

const INSTITUTE_ADDRESS =
    "VOCATIONAL & TRADE TRAINING INSTITUTE • LIBERIA";

const CERTIFICATE_TITLE =
    "CERTIFICATE OF COMPLETION";

const CERTIFICATE_CREDENTIAL =
    "PRACTICAL SKILLS CERTIFICATION";


const COLORS = {
    deepForest: "#052E16",
    forest: "#14532D",
    green: "#15803D",
    emerald: "#16A34A",
    brightGreen: "#22C55E",
    freshGreen: "#4ADE80",

    gold: "#D4A017",
    brightGold: "#FACC15",
    amber: "#B7791F",

    black: "#07110A",
    charcoal: "#17211A",

    white: "#FFFFFF",
    cream: "#FAFCF9",

    paper: "#FDFEFD",
    soft: "#F3F8F4",
    softGreen: "#EAF6ED",
    softGold: "#FFF8DC",

    text: "#14251A",
    muted: "#66736A",

    border: "#CBDDCF",
    deepBorder: "#9FB7A5",
};


const getDisplayValue = (value) => {
    if (
        value === null ||
        value === undefined
    ) {
        return "";
    }

    if (
        typeof value === "object"
    ) {
        return (
            value.name ||
            value.title ||
            value.label ||
            value.value ||
            ""
        );
    }

    return String(value);
};


const getStudentName = (student) => {
    return (
        getDisplayValue(
            student?.full_name
        ) ||
        getDisplayValue(
            student?.name
        ) ||
        getDisplayValue(
            student?.student_name
        ) ||
        "Graduate"
    );
};


const getClassName = (gradeClass) => {
    return (
        getDisplayValue(
            gradeClass?.name
        ) ||
        "Vocational Skills Training Program"
    );
};


const getDivision = (gradeClass) => {
    return getDisplayValue(
        gradeClass?.division
    );
};


const getAcademicYear = (
    academicYear
) => {
    return (
        getDisplayValue(
            academicYear?.name
        ) ||
        "Training Year"
    );
};


const getCertificateNumber = (
    student,
    academicYear
) => {

    const year =
        getAcademicYear(
            academicYear
        );

    const yearDigits =
        String(year)
            .replace(
                /[^0-9]/g,
                ""
            )
            .slice(-4) ||
        "2026";

    const studentId =
        student?.id ||
        student?.student_id ||
        "001";

    return `ISV-${yearDigits}-${studentId}`;
};


const styles = StyleSheet.create({

    page: {
        width: "100%",
        height: "100%",
        backgroundColor: COLORS.paper,
        fontFamily: "Helvetica",
        padding: 24,
    },


    outerFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 2,
        borderColor: COLORS.deepForest,
        padding: 4,
        backgroundColor: COLORS.cream,
    },


    greenFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 1.5,
        borderColor: COLORS.green,
        padding: 4,
    },


    innerFrame: {
        width: "100%",
        height: "100%",
        borderWidth: 1,
        borderColor: COLORS.deepBorder,
        backgroundColor: COLORS.paper,
        overflow: "hidden",
        position: "relative",
    },


    topTechnicalBand: {
        width: "100%",
        height: 18,
        flexDirection: "row",
    },


    topDark: {
        width: "22%",
        height: 18,
        backgroundColor: COLORS.deepForest,
    },


    topGreen: {
        width: "43%",
        height: 18,
        backgroundColor: COLORS.green,
    },


    topBright: {
        width: "17%",
        height: 18,
        backgroundColor: COLORS.brightGreen,
    },


    topGold: {
        width: "18%",
        height: 18,
        backgroundColor: COLORS.gold,
    },


    bottomTechnicalBand: {
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 0,
        height: 12,
        flexDirection: "row",
    },


    bottomDark: {
        width: "32%",
        backgroundColor: COLORS.deepForest,
    },


    bottomGreen: {
        width: "38%",
        backgroundColor: COLORS.green,
    },


    bottomGold: {
        width: "18%",
        backgroundColor: COLORS.gold,
    },


    bottomBright: {
        width: "12%",
        backgroundColor: COLORS.brightGreen,
    },


    content: {
        flex: 1,
        paddingHorizontal: 34,
        paddingTop: 14,
        paddingBottom: 16,
    },


    header: {
        width: "100%",
        minHeight: 58,
        flexDirection: "row",
        alignItems: "center",
    },


    logoArea: {
        width: 72,
        height: 58,
        alignItems: "flex-start",
        justifyContent: "center",
    },


    logo: {
        width: 52,
        height: 52,
        objectFit: "contain",
    },


    organizationArea: {
        flex: 1,
        paddingLeft: 3,
        paddingRight: 18,
    },


    organization: {
        color: COLORS.deepForest,
        fontSize: 17,
        fontWeight: "bold",
        letterSpacing: 1.8,
    },


    organizationSub: {
        marginTop: 4,
        color: COLORS.green,
        fontSize: 6.8,
        fontWeight: "bold",
        letterSpacing: 1,
    },


    address: {
        marginTop: 4,
        color: COLORS.muted,
        fontSize: 6,
        letterSpacing: 0.55,
    },


    headerTechnicalMark: {
        width: 145,
        height: 52,
        alignItems: "flex-end",
        justifyContent: "center",
    },


    technicalLineOne: {
        width: 118,
        height: 3,
        backgroundColor: COLORS.deepForest,
        marginBottom: 4,
    },


    technicalLineTwo: {
        width: 92,
        height: 2,
        backgroundColor: COLORS.green,
        marginBottom: 4,
    },


    technicalLineThree: {
        width: 66,
        height: 2,
        backgroundColor: COLORS.gold,
        marginBottom: 5,
    },


    technicalLabel: {
        color: COLORS.muted,
        fontSize: 5.2,
        letterSpacing: 1.1,
        fontWeight: "bold",
    },


    headingArea: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 8,
    },


    credential: {
        color: COLORS.green,
        fontSize: 6.8,
        fontWeight: "bold",
        letterSpacing: 2,
        textAlign: "center",
    },


    certificateTitle: {
        marginTop: 3,
        color: COLORS.deepForest,
        fontSize: 24,
        fontWeight: "bold",
        letterSpacing: 1.35,
        textAlign: "center",
    },


    technicalDivider: {
        width: 300,
        height: 14,
        marginTop: 3,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },


    dividerLineLeft: {
        width: 125,
        height: 1,
        backgroundColor: COLORS.deepBorder,
    },


    dividerLineRight: {
        width: 125,
        height: 1,
        backgroundColor: COLORS.deepBorder,
    },


    dividerBlock: {
        width: 12,
        height: 7,
        marginHorizontal: 8,
        backgroundColor: COLORS.green,
    },


    dividerBlockGold: {
        width: 6,
        height: 12,
        backgroundColor: COLORS.gold,
        transform: [
            {
                rotate: "45deg",
            },
        ],
    },


    recipientSection: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
        paddingHorizontal: 12,
    },


    bodyIntro: {
        color: COLORS.muted,
        fontSize: 7.3,
        letterSpacing: 0.5,
        textAlign: "center",
    },


    studentName: {
        marginTop: 5,
        color: COLORS.deepForest,
        fontSize: 29,
        fontWeight: "bold",
        letterSpacing: 0.65,
        textAlign: "center",
        maxWidth: 590,
    },


    nameDecoration: {
        width: 410,
        height: 12,
        marginTop: 3,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },


    nameLineLeft: {
        width: 165,
        height: 1,
        backgroundColor: COLORS.green,
    },


    nameLineRight: {
        width: 165,
        height: 1,
        backgroundColor: COLORS.green,
    },


    nameCenter: {
        width: 9,
        height: 9,
        marginHorizontal: 10,
        backgroundColor: COLORS.gold,
        transform: [
            {
                rotate: "45deg",
            },
        ],
    },


    recognitionArea: {
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        marginTop: 1,
    },


    recognition: {
        maxWidth: 535,
        color: COLORS.text,
        fontSize: 7.3,
        lineHeight: 1.45,
        textAlign: "center",
    },


    skillBanner: {
        width: "100%",
        minHeight: 49,
        marginTop: 8,
        flexDirection: "row",
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.softGreen,
    },


    skillAccent: {
        width: 8,
        backgroundColor: COLORS.green,
    },


    skillMain: {
        flex: 1,
        paddingVertical: 7,
        paddingHorizontal: 14,
        justifyContent: "center",
    },


    skillLabel: {
        color: COLORS.green,
        fontSize: 5.8,
        fontWeight: "bold",
        letterSpacing: 1.25,
    },


    skillName: {
        marginTop: 3,
        color: COLORS.deepForest,
        fontSize: 11.5,
        fontWeight: "bold",
        letterSpacing: 0.25,
    },


    division: {
        marginTop: 2,
        color: COLORS.muted,
        fontSize: 6.4,
    },


    trainingYear: {
        width: 128,
        borderLeftWidth: 1,
        borderLeftColor: COLORS.border,
        backgroundColor: COLORS.softGold,
        alignItems: "center",
        justifyContent: "center",
    },


    yearLabel: {
        color: COLORS.muted,
        fontSize: 5.5,
        fontWeight: "bold",
        letterSpacing: 1,
    },


    yearValue: {
        marginTop: 4,
        color: COLORS.deepForest,
        fontSize: 9.5,
        fontWeight: "bold",
        letterSpacing: 0.4,
    },


    lowerArea: {
        width: "100%",
        minHeight: 47,
        marginTop: 7,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },


    informationBox: {
        width: 170,
        height: 36,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.paper,
        justifyContent: "center",
        paddingHorizontal: 10,
    },


    informationLabel: {
        color: COLORS.muted,
        fontSize: 5.1,
        letterSpacing: 0.8,
    },


    informationValue: {
        marginTop: 3,
        color: COLORS.deepForest,
        fontSize: 6.8,
        fontWeight: "bold",
        letterSpacing: 0.5,
    },


    badgeArea: {
        flex: 1,
        height: 48,
        alignItems: "center",
        justifyContent: "center",
    },


    badge: {
        width: 50,
        height: 50,
    },


    completionBox: {
        width: 170,
        height: 36,
        borderWidth: 1,
        borderColor: COLORS.border,
        backgroundColor: COLORS.softGreen,
        justifyContent: "center",
        alignItems: "flex-end",
        paddingHorizontal: 10,
    },


    completionLabel: {
        color: COLORS.muted,
        fontSize: 5.1,
        letterSpacing: 0.8,
    },


    completionValue: {
        marginTop: 3,
        color: COLORS.green,
        fontSize: 6.6,
        fontWeight: "bold",
        letterSpacing: 0.45,
    },


    signatures: {
        width: "100%",
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between",
        marginTop: 7,
    },


    signature: {
        width: 175,
        alignItems: "center",
    },


    signatureLine: {
        width: 148,
        borderTopWidth: 1,
        borderTopColor: COLORS.deepForest,
        marginBottom: 3,
    },


    signatureName: {
        color: COLORS.deepForest,
        fontSize: 6.2,
        fontWeight: "bold",
        letterSpacing: 0.3,
        textAlign: "center",
    },


    signatureRole: {
        marginTop: 2,
        color: COLORS.green,
        fontSize: 5.1,
        letterSpacing: 0.4,
        textAlign: "center",
    },


    centerMark: {
        width: 155,
        alignItems: "center",
        justifyContent: "center",
    },


    centerMarkText: {
        color: COLORS.deepForest,
        fontSize: 5.1,
        fontWeight: "bold",
        letterSpacing: 0.75,
        textAlign: "center",
    },


    centerMarkSub: {
        marginTop: 3,
        color: COLORS.muted,
        fontSize: 4.7,
        letterSpacing: 0.45,
        textAlign: "center",
    },


    footerNote: {
        marginTop: 4,
        alignItems: "center",
    },


    footerNoteText: {
        color: COLORS.muted,
        fontSize: 4.5,
        letterSpacing: 0.6,
        textAlign: "center",
    },

});


const TechnicalCorner = ({
    flipX = false,
    flipY = false,
}) => {

    const transform = [];

    if (flipX) {
        transform.push({
            scaleX: -1,
        });
    }

    if (flipY) {
        transform.push({
            scaleY: -1,
        });
    }

    return (
        <Svg
            width="112"
            height="84"
            viewBox="0 0 112 84"
            style={{
                transform,
            }}
        >

            <Polygon
                points="0,0 112,0 0,84"
                fill={COLORS.deepForest}
            />

            <Polygon
                points="0,0 83,0 0,63"
                fill={COLORS.green}
            />

            <Polygon
                points="0,0 51,0 0,39"
                fill={COLORS.gold}
            />

            <Line
                x1="13"
                y1="14"
                x2="76"
                y2="14"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.3"
            />

            <Line
                x1="13"
                y1="14"
                x2="13"
                y2="65"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.24"
            />

            <Rect
                x="12"
                y="11"
                width="6"
                height="6"
                fill={COLORS.brightGold}
            />

            <Circle
                cx="30"
                cy="30"
                r="2.3"
                fill={COLORS.white}
                opacity="0.55"
            />

            <Circle
                cx="39"
                cy="39"
                r="2.3"
                fill={COLORS.white}
                opacity="0.4"
            />

        </Svg>
    );
};


const SkillsBadge = () => {

    return (
        <Svg
            width="50"
            height="50"
            viewBox="0 0 100 100"
        >

            <Circle
                cx="50"
                cy="50"
                r="46"
                fill={COLORS.deepForest}
            />

            <Circle
                cx="50"
                cy="50"
                r="41"
                fill="none"
                stroke={COLORS.gold}
                strokeWidth="2"
            />

            <Circle
                cx="50"
                cy="50"
                r="35"
                fill={COLORS.green}
            />

            <Circle
                cx="50"
                cy="50"
                r="29"
                fill="none"
                stroke={COLORS.brightGreen}
                strokeWidth="1"
            />

            <Polygon
                points="
                    50,18
                    55,32
                    70,32
                    58,41
                    63,55
                    50,47
                    37,55
                    42,41
                    30,32
                    45,32
                "
                fill={COLORS.brightGold}
            />

            <Circle
                cx="50"
                cy="39"
                r="9"
                fill={COLORS.deepForest}
            />

            <Path
                d="
                    M44 39
                    L48 43
                    L57 33
                "
                fill="none"
                stroke={COLORS.white}
                strokeWidth="2.5"
            />

            <Line
                x1="32"
                y1="66"
                x2="68"
                y2="66"
                stroke={COLORS.white}
                strokeWidth="1"
                opacity="0.8"
            />

            <Line
                x1="38"
                y1="72"
                x2="62"
                y2="72"
                stroke={COLORS.gold}
                strokeWidth="1.5"
            />

        </Svg>
    );
};


const TechnicalHeaderMark = () => {

    return (
        <Svg
            width="145"
            height="52"
            viewBox="0 0 145 52"
        >

            <Line
                x1="27"
                y1="9"
                x2="145"
                y2="9"
                stroke={COLORS.deepForest}
                strokeWidth="3"
            />

            <Line
                x1="53"
                y1="17"
                x2="145"
                y2="17"
                stroke={COLORS.green}
                strokeWidth="2"
            />

            <Line
                x1="79"
                y1="25"
                x2="145"
                y2="25"
                stroke={COLORS.gold}
                strokeWidth="2"
            />

            <Rect
                x="9"
                y="7"
                width="12"
                height="12"
                fill={COLORS.green}
            />

            <Rect
                x="31"
                y="15"
                width="8"
                height="8"
                fill={COLORS.gold}
            />

            <Circle
                cx="62"
                cy="34"
                r="3"
                fill={COLORS.deepForest}
            />

            <Circle
                cx="76"
                cy="34"
                r="3"
                fill={COLORS.green}
            />

            <Circle
                cx="90"
                cy="34"
                r="3"
                fill={COLORS.gold}
            />

            <Line
                x1="103"
                y1="34"
                x2="145"
                y2="34"
                stroke={COLORS.deepBorder}
                strokeWidth="1"
            />

        </Svg>
    );
};


const CertificatePage = ({
    student,
    gradeClass,
    academicYear,
}) => {

    const studentName =
        getStudentName(student);

    const className =
        getClassName(gradeClass);

    const division =
        getDivision(gradeClass);

    const year =
        getAcademicYear(
            academicYear
        );

    const certificateNumber =
        getCertificateNumber(
            student,
            academicYear
        );

    return (
        <Document>

            <Page
                size="A4"
                orientation="landscape"
                style={styles.page}
            >

                <View
                    style={styles.outerFrame}
                >

                    <View
                        style={styles.greenFrame}
                    >

                        <View
                            style={styles.innerFrame}
                        >

                            <View
                                style={
                                    styles.topTechnicalBand
                                }
                            >

                                <View
                                    style={
                                        styles.topDark
                                    }
                                />

                                <View
                                    style={
                                        styles.topGreen
                                    }
                                />

                                <View
                                    style={
                                        styles.topBright
                                    }
                                />

                                <View
                                    style={
                                        styles.topGold
                                    }
                                />

                            </View>


                            <View
                                style={{
                                    position:
                                        "absolute",
                                    left: 0,
                                    top: 18,
                                }}
                            >
                                <TechnicalCorner />
                            </View>


                            <View
                                style={{
                                    position:
                                        "absolute",
                                    right: 0,
                                    top: 18,
                                }}
                            >
                                <TechnicalCorner
                                    flipX
                                />
                            </View>


                            <View
                                style={{
                                    position:
                                        "absolute",
                                    left: 0,
                                    bottom: 12,
                                }}
                            >
                                <TechnicalCorner
                                    flipY
                                />
                            </View>


                            <View
                                style={{
                                    position:
                                        "absolute",
                                    right: 0,
                                    bottom: 12,
                                }}
                            >
                                <TechnicalCorner
                                    flipX
                                    flipY
                                />
                            </View>


                            <View
                                style={styles.content}
                            >

                                <View
                                    style={styles.header}
                                >

                                    <View
                                        style={
                                            styles.logoArea
                                        }
                                    >

                                        <Image
                                            src={
                                                SCHOOL_LOGO
                                            }
                                            style={
                                                styles.logo
                                            }
                                        />

                                    </View>


                                    <View
                                        style={
                                            styles.organizationArea
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.organization
                                            }
                                        >
                                            {INSTITUTE_NAME}
                                        </Text>

                                        <Text
                                            style={
                                                styles.organizationSub
                                            }
                                        >
                                            {INSTITUTE_SUBTITLE}
                                        </Text>

                                        <Text
                                            style={
                                                styles.address
                                            }
                                        >
                                            {INSTITUTE_ADDRESS}
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.headerTechnicalMark
                                        }
                                    >

                                        <TechnicalHeaderMark />

                                        <Text
                                            style={
                                                styles.technicalLabel
                                            }
                                        >
                                            SKILLS • CRAFT • INDUSTRY
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.headingArea
                                    }
                                >

                                    <Text
                                        style={
                                            styles.credential
                                        }
                                    >
                                        {CERTIFICATE_CREDENTIAL}
                                    </Text>

                                    <Text
                                        style={
                                            styles.certificateTitle
                                        }
                                    >
                                        {CERTIFICATE_TITLE}
                                    </Text>


                                    <View
                                        style={
                                            styles.technicalDivider
                                        }
                                    >

                                        <View
                                            style={
                                                styles.dividerLineLeft
                                            }
                                        />

                                        <View
                                            style={
                                                styles.dividerBlock
                                            }
                                        />

                                        <View
                                            style={
                                                styles.dividerBlockGold
                                            }
                                        />

                                        <View
                                            style={
                                                styles.dividerLineRight
                                            }
                                        />

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.recipientSection
                                    }
                                >

                                    <Text
                                        style={
                                            styles.bodyIntro
                                        }
                                    >
                                        This certificate is proudly
                                        awarded to
                                    </Text>


                                    <Text
                                        style={
                                            styles.studentName
                                        }
                                    >
                                        {studentName}
                                    </Text>


                                    <View
                                        style={
                                            styles.nameDecoration
                                        }
                                    >

                                        <View
                                            style={
                                                styles.nameLineLeft
                                            }
                                        />

                                        <View
                                            style={
                                                styles.nameCenter
                                            }
                                        />

                                        <View
                                            style={
                                                styles.nameLineRight
                                            }
                                        />

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.recognitionArea
                                    }
                                >

                                    <Text
                                        style={
                                            styles.recognition
                                        }
                                    >
                                        In recognition of successfully
                                        completing the prescribed
                                        vocational training requirements
                                        and demonstrating practical
                                        knowledge, technical ability,
                                        discipline, and the competencies
                                        required within the completed
                                        trade training program.
                                    </Text>

                                </View>


                                <View
                                    style={
                                        styles.skillBanner
                                    }
                                >

                                    <View
                                        style={
                                            styles.skillAccent
                                        }
                                    />


                                    <View
                                        style={
                                            styles.skillMain
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.skillLabel
                                            }
                                        >
                                            TRADE / SKILLS TRAINING PROGRAM
                                        </Text>

                                        <Text
                                            style={
                                                styles.skillName
                                            }
                                        >
                                            {className}
                                        </Text>

                                        {division ? (
                                            <Text
                                                style={
                                                    styles.division
                                                }
                                            >
                                                {division}
                                            </Text>
                                        ) : null}

                                    </View>


                                    <View
                                        style={
                                            styles.trainingYear
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.yearLabel
                                            }
                                        >
                                            TRAINING YEAR
                                        </Text>

                                        <Text
                                            style={
                                                styles.yearValue
                                            }
                                        >
                                            {year}
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.lowerArea
                                    }
                                >

                                    <View
                                        style={
                                            styles.informationBox
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.informationLabel
                                            }
                                        >
                                            CERTIFICATE NUMBER
                                        </Text>

                                        <Text
                                            style={
                                                styles.informationValue
                                            }
                                        >
                                            {certificateNumber}
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.badgeArea
                                        }
                                    >

                                        <SkillsBadge />

                                    </View>


                                    <View
                                        style={
                                            styles.completionBox
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.completionLabel
                                            }
                                        >
                                            CERTIFICATION STATUS
                                        </Text>

                                        <Text
                                            style={
                                                styles.completionValue
                                            }
                                        >
                                            TRAINING COMPLETED
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.signatures
                                    }
                                >

                                    <View
                                        style={
                                            styles.signature
                                        }
                                    >

                                        <View
                                            style={
                                                styles.signatureLine
                                            }
                                        />

                                        <Text
                                            style={
                                                styles.signatureName
                                            }
                                        >
                                            AUTHORIZED DIRECTOR
                                        </Text>

                                        <Text
                                            style={
                                                styles.signatureRole
                                            }
                                        >
                                            IGNITE S VOCATIONAL SCHOOL
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.centerMark
                                        }
                                    >

                                        <Text
                                            style={
                                                styles.centerMarkText
                                            }
                                        >
                                            VOCATIONAL SKILLS
                                        </Text>

                                        <Text
                                            style={
                                                styles.centerMarkText
                                            }
                                        >
                                            CERTIFICATION
                                        </Text>

                                        <Text
                                            style={
                                                styles.centerMarkSub
                                            }
                                        >
                                            Practical Training • Technical Skills • Career Development
                                        </Text>

                                    </View>


                                    <View
                                        style={
                                            styles.signature
                                        }
                                    >

                                        <View
                                            style={
                                                styles.signatureLine
                                            }
                                        />

                                        <Text
                                            style={
                                                styles.signatureName
                                            }
                                        >
                                            TRAINING REGISTRAR
                                        </Text>

                                        <Text
                                            style={
                                                styles.signatureRole
                                            }
                                        >
                                            CERTIFICATION & RECORDS
                                        </Text>

                                    </View>

                                </View>


                                <View
                                    style={
                                        styles.footerNote
                                    }
                                >

                                    <Text
                                        style={
                                            styles.footerNoteText
                                        }
                                    >
                                        IGNITE S VOCATIONAL SCHOOL • TRADE TRAINING • PRACTICAL SKILLS • PROFESSIONAL EXCELLENCE
                                    </Text>

                                </View>

                            </View>


                            <View
                                style={
                                    styles.bottomTechnicalBand
                                }
                            >

                                <View
                                    style={
                                        styles.bottomDark
                                    }
                                />

                                <View
                                    style={
                                        styles.bottomGreen
                                    }
                                />

                                <View
                                    style={
                                        styles.bottomGold
                                    }
                                />

                                <View
                                    style={
                                        styles.bottomBright
                                    }
                                />

                            </View>

                        </View>

                    </View>

                </View>

            </Page>

        </Document>
    );
};


const GraduateCertificatePDF = {

    downloadSingle: async (
        student,
        gradeClass,
        academicYear
    ) => {

        const blob =
            await pdf(
                <CertificatePage
                    student={student}
                    gradeClass={
                        gradeClass
                    }
                    academicYear={
                        academicYear
                    }
                />
            ).toBlob();


        const fileName =
            `${getStudentName(student)
                .replace(
                    /\s+/g,
                    "_"
                )
            }_IgniteS_Vocational_Certificate.pdf`;


        saveAs(
            blob,
            fileName
        );
    },


    downloadMultiple: async (
        students,
        gradeClass,
        academicYear
    ) => {

        const zip =
            new JSZip();


        for (
            const student of students
        ) {

            const blob =
                await pdf(
                    <CertificatePage
                        student={student}
                        gradeClass={
                            gradeClass
                        }
                        academicYear={
                            academicYear
                        }
                    />
                ).toBlob();


            const fileName =
                `${getStudentName(student)
                    .replace(
                        /\s+/g,
                        "_"
                    )
                }_IgniteS_Vocational_Certificate.pdf`;


            zip.file(
                fileName,
                blob
            );
        }


        const zipBlob =
            await zip.generateAsync({
                type: "blob",
            });


        saveAs(
            zipBlob,
            "IgniteS_Vocational_Training_Certificates.zip"
        );
    },
};


export default GraduateCertificatePDF;